import { createColumnHelper } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { useGetCampaigns, useGetUserCoupons } from "../../queries/campaign";
import { useGetActiveOrganiser, useGetAllUsers } from "../../queries/user";
import { useMemo, useState, useEffect } from "react";
import Chip from "../../components/Chip";
import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from "react-toastify";
import TanStackTable from "../../components/TanStackTable";
import { FaEye, FaPlay, FaClock } from "react-icons/fa";
import { ActionButton } from '../../components/Forms/ActionButton';
import { runPullApi, freeExpiredCoupons } from "../../api/campaign";

const UserCoupons = () => {
    const navigate = useNavigate();

    // Filter states
    const [filteredData, setFilteredData] = useState([]);
    const [filters, setFilters] = useState({
        campaign: '',
        sponsor: '',
        user: '',
        status: ''
    });
    const [showTransactionModal, setShowTransactionModal] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [runningPullApi, setRunningPullApi] = useState(false);
    const [freeingCoupons, setFreeingCoupons] = useState(false);

    // Data fetching
    const { data: couponsData, isPending } = useGetUserCoupons();
    const { data: campaignsData } = useGetCampaigns();
    const { data: organiserList = [] } = useGetActiveOrganiser();
    const { data: usersList = [] } = useGetAllUsers();

    // Transform coupon data to user coupon format
    const userCouponsData = useMemo(() => {
        if (!couponsData) return [];
        
        const userCoupons = [];
        couponsData.forEach((userCouponDetail, index) => {
            userCoupons.push({
                _id: userCouponDetail._id,
                couponId: userCouponDetail.couponId?._id,
                couponCode: userCouponDetail.couponId?.code,
                campaignId: userCouponDetail.campaignId,
                campaignName: userCouponDetail.campaignId?.title || 'N/A',
                userId: userCouponDetail.userId,
                username: userCouponDetail.userId?.username || `${userCouponDetail.userId?.name?.firstName} ${userCouponDetail.userId?.name?.lastName}`.trim() || 'Unknown User',
                userEmail: userCouponDetail.userId?.email || 'N/A',
                assignedAt: userCouponDetail.createdAt,
                redeemedAt: userCouponDetail.redeemedAt,
                isRedeemed: userCouponDetail.isRedeemed,
                status: userCouponDetail.status,
                couponHookTransactionId: userCouponDetail.couponHookTransactionId,
                couponHookTransaction: userCouponDetail.couponHookTransactionId, // Updated to use the full transaction object
                productId: userCouponDetail.productId,
                sponsor: userCouponDetail.campaignId?.companyId?.companyName || userCouponDetail.campaignId?.NGOId?.ngoName || 'N/A',
                sponsorType: userCouponDetail.campaignId?.companyId ? 'Company' : 'NGO',
                originalCouponData: userCouponDetail.couponId,
                originalUserDetail: userCouponDetail,
                startDate: userCouponDetail.startDate,
                endDate: userCouponDetail.endDate,
                userChallengeId: userCouponDetail.userChallengeId // Added userChallenge data
            });
        });
        
        return userCoupons.sort((a, b) => new Date(b.assignedAt) - new Date(a.assignedAt));
    }, [couponsData]);

    useEffect(() => {
        setFilteredData(userCouponsData);
    }, [userCouponsData]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        let filtered = userCouponsData;

        // Campaign filter
        if (filters.campaign) {
            filtered = filtered.filter((userCoupon) =>
                userCoupon.campaignId?._id === filters.campaign
            );
        }

        // Sponsor filter
        if (filters.sponsor) {
            filtered = filtered.filter((userCoupon) => {
                const sponsorCompanyId = userCoupon.campaignId?.companyId?._id;
                const sponsorNgoId = userCoupon.campaignId?.NGOId?._id;
                const sponsorId = sponsorCompanyId || sponsorNgoId;
                return sponsorId === filters.sponsor;
            });
        }

        // User filter
        if (filters.user) {
            filtered = filtered.filter((userCoupon) =>
                userCoupon.userId?._id === filters.user
            );
        }

        // Status filter
        if (filters.status) {
            if (filters.status === 'Assigned') {
                filtered = filtered.filter((userCoupon) => 
                    userCoupon.status === 'assigned' && !userCoupon.isRedeemed
                );
            } else if (filters.status === 'Redeemed') {
                filtered = filtered.filter((userCoupon) => 
                    userCoupon.isRedeemed === true
                );
            }
        }

        setFilteredData(filtered);
    };

    const handleViewTransaction = (userCoupon) => {
        setSelectedTransaction(userCoupon);
        setShowTransactionModal(true);
    };

    const handleRunPullApi = async () => {
        if (!filters.campaign) {
            toast.error("Please select a campaign first to run Pull API");
            return;
        }
        setRunningPullApi(true);
        try {
            const result = await runPullApi(filters.campaign);
            toast.success(result.message || "Pull API executed successfully");
            // Refetch data
            window.location.reload();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Pull API execution failed");
        } finally {
            setRunningPullApi(false);
        }
    };

    const handleFreeExpiredCoupons = async () => {
        if (!filters.campaign) {
            toast.error("Please select a campaign first to free expired coupons");
            return;
        }
        setFreeingCoupons(true);
        try {
            const result = await freeExpiredCoupons(filters.campaign);
            toast.success(result.message || "Expired coupons freed successfully");
            window.location.reload();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to free expired coupons");
        } finally {
            setFreeingCoupons(false);
        }
    };

    // Prepare campaign options
    const campaignOptions = useMemo(() => {
        if (!campaignsData) return [];
        return campaignsData.map(campaign => ({
            value: campaign._id,
            label: campaign.title
        }));
    }, [campaignsData]);

    // Prepare sponsor options from organiser list
    const sponsorOptions = useMemo(() => {
        if (!organiserList) return [];
        return organiserList.map(organiser => ({
            value: organiser._id,
            label: organiser.companyName || organiser.ngoName || organiser.name || 'Unknown'
        }));
    }, [organiserList]);

    // Prepare user options
    const userOptions = useMemo(() => {
        if (!usersList) return [];
        return usersList.map(user => ({
            value: user._id,
            label: `${user.username} (${user.email})`
        }));
    }, [usersList]);

    const columnHelper = createColumnHelper();

    const columns = useMemo(() => [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => info.row.index + 1,
        }),
        columnHelper.accessor('campaignName', {
            header: 'Campaign Name',
            cell: info => (
                <div className="whitespace-normal break-words line-clamp-2">
                    {info.getValue() || 'N/A'}
                </div>
            ),
        }),
        columnHelper.accessor('username', {
            header: 'Username',
            cell: info => (
                <div className="whitespace-normal break-words">
                    <div className="font-medium">{info.getValue() || 'Unknown User'}</div>
                    <div className="text-sm text-secondary">{info.row.original.userEmail}</div>
                </div>
            ),
        }),
        columnHelper.accessor('couponCodeFormat', {
            header: 'Coupon Type',
            align: 'center',
            cell: info => (
                <div className="whitespace-normal break-words">
                    <div className="font-medium">{info.row.original?.campaignId?.couponCodeFormat || 'Unknown Type'}</div>
                </div>
            ),
        }),
        columnHelper.accessor('couponCode', {
            header: 'Coupon Code',
            cell: info => (
                <div className="whitespace-normal break-words font-mono text-sm bg-navBtn px-2 py-1 rounded">
                    {Array.isArray(info.getValue()) ? info.getValue().join(', ') : (info.getValue() || 'N/A')}
                </div>
            ),
        }),
        columnHelper.accessor('assignedAt', {
            header: 'Assigned On',
            align: 'center',
            cell: info => {
                const date = info.getValue();
                if (!date) return 'N/A';
                const assignedDate = new Date(date);
                return (
                    <div className="whitespace-normal break-words text-center">
                        <div>{assignedDate.toLocaleDateString()}</div>
                        <div className="text-sm text-secondary">
                            {assignedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                    </div>
                );
            },
        }),
        columnHelper.accessor('redeemedAt', {
            header: 'Redeemed On',
            align: 'center',
            cell: info => {
                const date = info.getValue();
                if (!date || !info.row.original.isRedeemed) {
                    return (
                        <div className="text-center text-gray-400">
                            <Chip value="Not Redeemed" color="yellow" />
                        </div>
                    );
                }
                const redeemedDate = new Date(date);
                return (
                    <div className="whitespace-normal break-words text-center">
                        <div>{redeemedDate.toLocaleDateString()}</div>
                        <div className="text-sm text-secondary">
                            {redeemedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                    </div>
                );
            },
        }),
        columnHelper.accessor('endDate', {
            header: 'Expires On',
            align: 'center',
            cell: info => {
                const date = info.getValue();
                if (!date) return 'N/A';
                const expiryDate = new Date(date);
                const isExpired = expiryDate < new Date();
                return (
                    <div className="whitespace-normal break-words text-center">
                        <div className={isExpired ? 'text-red-600 font-medium' : ''}>{expiryDate.toLocaleDateString()}</div>
                        <div className="text-sm text-secondary">
                            {expiryDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        {isExpired && (
                            <div className="text-xs text-red-500 mt-1">Expired</div>
                        )}
                    </div>
                );
            },
        }),
        columnHelper.accessor('actions', {
            header: () => <div className="text-center">Actions</div>,
            enableColumnFilter: false,
            cell: info => {
                const userCoupon = info.row.original;
                return (
                    <div className='flex items-center justify-center'>
                        {userCoupon.isRedeemed && (
                            <button
                                className="rounded p-1.5 bg-[rgba(52,199,89,0.1)] hover:bg-[rgba(52,199,89,0.2)] mx-1 min-h-7 min-w-7"
                                onClick={() => handleViewTransaction(userCoupon)}
                                title="View Transaction Details"
                            >
                                <FaEye className="fill-primary" />
                            </button>
                        )}
                        {!userCoupon.isRedeemed && (
                            <div className="text-gray-400 text-sm">No actions</div>
                        )}
                    </div>
                )
            },
        }),
    ], [handleViewTransaction]);

    if (isPending) {
        return (
            <main className="flex flex-col text-base font-semibold text-darkGrayBg rounded-none">
                <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                    <div className="flex flex-col pr-px pl-6 w-full max-md:pl-5 max-md:max-w-full">
                        <h1 className="self-start text-lg font-semibold">User Coupons</h1>
                        <p>Loading...</p>
                    </div>
                </section>
            </main>
        );
    }

    return (
        <main className="flex flex-col text-base font-semibold text-darkGrayBg rounded-none">
            {/* Filters */}
            <form
                className="flex flex-col pt-5 w-full bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)]"
                onSubmit={handleSearch}
            >
                <div className='flex items-center justify-between pr-5'>
                    <div className="flex gap-1.5 items-start my-auto text-base whitespace-nowrap ml-5">
                        <img
                            loading="lazy"
                            src="https://cdn.builder.io/api/v1/image/assets/TEMP/4dd39feef0ff4934d9e2e86b8c7285d5d7532aeb26adcdc1e878d021439e2681?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
                            alt=""
                            className="object-contain shrink-0 w-5 aspect-square"
                        />
                        <span className="self-stretch">Filters</span>
                        <img
                            loading="lazy"
                            src="https://cdn.builder.io/api/v1/image/assets/TEMP/d18f51a0b2d597a302f55b77fc1071043eab5f274a1cc9e9b3fe3e2c97520a8c?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
                            alt=""
                            className="object-contain shrink-0 aspect-square w-[18px]"
                        />
                    </div>
                </div>
                <div className="flex flex-wrap gap-4 p-5">
                    <div className='flex gap-3 w-full flex-wrap'>
                        {/* Campaign Filter */}
                        <div className="flex-col w-full md:w-auto md:flex-1">
                            <label htmlFor="campaign" className="gap-1 self-stretch w-full text-darkGrayBg text-base">
                                Campaign
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <select
                                    name="campaign"
                                    value={filters.campaign}
                                    onChange={handleFilterChange}
                                    className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                >
                                    <option value="">All Campaigns</option>
                                    {campaignOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Sponsor Filter */}
                        <div className="flex-col w-full md:w-auto md:flex-1">
                            <label htmlFor="sponsor" className="gap-1 self-stretch w-full text-darkGrayBg text-base">
                                Sponsor
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <select
                                    name="sponsor"
                                    value={filters.sponsor}
                                    onChange={handleFilterChange}
                                    className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                >
                                    <option value="">All Sponsors</option>
                                    {sponsorOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* User Filter */}
                        <div className="flex-col w-full md:w-auto md:flex-1">
                            <label htmlFor="user" className="gap-1 self-stretch w-full text-darkGrayBg text-base">
                                User
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <select
                                    name="user"
                                    value={filters.user}
                                    onChange={handleFilterChange}
                                    className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                >
                                    <option value="">All Users</option>
                                    {userOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Status Filter */}
                        <div className="flex-col w-full md:w-auto md:flex-1">
                            <label htmlFor="status" className="gap-1 self-stretch w-full text-darkGrayBg text-base">
                                Status
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <select
                                    name="status"
                                    value={filters.status}
                                    onChange={handleFilterChange}
                                    className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                >
                                    <option value="">All Status</option>
                                    <option value="Assigned">Assigned</option>
                                    <option value="Redeemed">Redeemed</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='flex gap-3 items-center justify-end p-5'>
                    <ActionButton
                        variant={'reset'}
                        text={'Reset'}
                        handleSubmit={() => {
                            setFilters({
                                campaign: '',
                                sponsor: '',
                                user: '',
                                status: ''
                            });
                            setFilteredData(userCouponsData);
                        }}
                    />
                    <ActionButton
                        variant={'submit'}
                        text={'Search'}
                        handleSubmit={() => { }}
                    />
                </div>
            </form>

            {/* Batch Process Buttons */}
            {filters.campaign && (
                <div style={{ display: 'flex', gap: '12px', marginTop: '16px', paddingLeft: '4px' }}>
                    <button
                        onClick={handleRunPullApi}
                        disabled={runningPullApi}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: runningPullApi ? '#93c5fd' : '#2563eb', color: '#ffffff', borderRadius: '6px', fontWeight: 600, fontSize: '14px', border: 'none', cursor: runningPullApi ? 'not-allowed' : 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
                    >
                        <FaPlay style={{ fontSize: '10px' }} />
                        {runningPullApi ? 'Running Pull API...' : 'Run Pull API'}
                    </button>
                    <button
                        onClick={handleFreeExpiredCoupons}
                        disabled={freeingCoupons}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: freeingCoupons ? '#fdba74' : '#ea580c', color: '#ffffff', borderRadius: '6px', fontWeight: 600, fontSize: '14px', border: 'none', cursor: freeingCoupons ? 'not-allowed' : 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
                    >
                        <FaClock style={{ fontSize: '10px' }} />
                        {freeingCoupons ? 'Freeing Coupons...' : 'Free Expired Coupons'}
                    </button>
                </div>
            )}

            <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                <div className="flex flex-col pr-px w-full max-md:max-w-full pl-6">
                    <h1 className="self-start text-lg font-bold pb-4">User Coupons</h1>
                    {filteredData && filteredData.length > 0 ? (
                        <TanStackTable
                            data={filteredData}
                            columns={columns}
                            enablePagination={true}
                        />
                    ) : (
                        <div className="pb-4">
                            No user coupons available
                        </div>
                    )}
                </div>
            </section>

            {/* Transaction Details Modal */}
            {showTransactionModal && selectedTransaction && (
                <TransactionModal
                    isOpen={showTransactionModal}
                    onClose={() => {
                        setShowTransactionModal(false);
                        setSelectedTransaction(null);
                    }}
                    transaction={selectedTransaction}
                />
            )}

            <ToastContainer
                position="top-right"
                autoClose={3000}
            />
        </main>
    );
}

// Transaction Modal Component
const TransactionModal = ({ isOpen, onClose, transaction }) => {
    if (!isOpen || !transaction) return null;

    const hookTransaction = transaction.couponHookTransaction;
    const hasTransactionData = hookTransaction && Object.keys(hookTransaction).length > 0;
    const userChallenge = transaction.userChallengeId;

    // Request headers from actual transaction data
    const requestHeaders = hasTransactionData ? hookTransaction.rawHeaders : {
        "auth-key": "your_auth_key_here",
        "client-secret": transaction.campaignId?.uniqueId || "campaign_client_secret",
        "content-type": "application/json",
        "user-agent": "Climate-Crusaders-Admin/1.0",
        "accept": "application/json"
    };

    // Request body from actual transaction data
    const requestBody = hasTransactionData ? hookTransaction.rawBody : null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-border">
                    <h2 className="text-xl font-bold text-darkGrayBg">Coupon Transaction Details</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl"
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Coupon Code</label>
                            <div className="p-3 bg-navBtn rounded border border-border font-mono text-sm">
                                {transaction.couponCode}
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Campaign</label>
                            <div className="p-3 bg-navBtn rounded border border-border text-sm">
                                {transaction.campaignName}
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                            <div className="p-3 bg-navBtn rounded border border-border text-sm">
                                {transaction.username}
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <div className="p-3 bg-navBtn rounded border border-border text-sm">
                                {transaction.userEmail}
                            </div>
                        </div>
                    </div>

                    {/* Status */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                            <div className="flex items-center gap-2">
                                <Chip 
                                    value={transaction.isRedeemed ? 'Redeemed' : 'Assigned'} 
                                    color={transaction.isRedeemed ? 'green' : 'blue'} 
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Sponsor</label>
                            <div className="p-3 bg-navBtn rounded border border-border text-sm">
                                {transaction.sponsor} ({transaction.sponsorType})
                            </div>
                        </div>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Assigned At</label>
                            <div className="p-3 bg-navBtn rounded border border-border text-sm">
                                {new Date(transaction.assignedAt).toLocaleString()}
                            </div>
                        </div>
                        {transaction.redeemedAt && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Redeemed At</label>
                                <div className="p-3 bg-primaryBg rounded border border-border text-sm">
                                    {new Date(transaction.redeemedAt).toLocaleString()}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* User Challenge Information */}
                    {userChallenge && (
                        <div className="border border-border rounded-lg p-4">
                            <h3 className="text-lg font-semibold text-darkGrayBg mb-4">🏆 Challenge Completion Details</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Points Earned</label>
                                    <div className="p-3 bg-green-50 rounded border border-green-200 text-sm font-medium text-green-800">
                                        {userChallenge.allocatedPoints || 0} Points
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Carbon Saving Points</label>
                                    <div className="p-3 bg-blue-50 rounded border border-blue-200 text-sm font-medium text-blue-800">
                                        {userChallenge.actualCarbonSavingPoints || 0} Points
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Carbon Saved</label>
                                    <div className="p-3 bg-purple-50 rounded border border-purple-200 text-sm font-medium text-purple-800">
                                        {userChallenge.carbonEmissionCount || 0} KG
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Review Status</label>
                                    <div className="p-3 bg-navBtn rounded border border-border text-sm">
                                        {userChallenge.reviewStatus || 'N/A'}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Submitted At</label>
                                    <div className="p-3 bg-navBtn rounded border border-border text-sm">
                                        {userChallenge.isSubmitted ? new Date(userChallenge.isSubmitted).toLocaleString() : 'N/A'}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Bonus Points</label>
                                    <div className="p-3 bg-yellow-50 rounded border border-yellow-200 text-sm font-medium text-yellow-800">
                                        {userChallenge.allocatedBonusPoints || 0} Points
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Show transaction data only if redeemed and has transaction data */}
                    {transaction.isRedeemed && hasTransactionData && (
                        <>
                            {/* Request Headers Section */}
                            <div className="border border-border rounded-lg p-4">
                                <h3 className="text-lg font-semibold text-darkGrayBg mb-4">📋 Request Headers</h3>
                                <div className="bg-darkGrayBg text-primary text-green-400 p-4 rounded font-mono text-sm overflow-x-auto">
                                    <pre>{JSON.stringify(requestHeaders, null, 2)}</pre>
                                </div>
                            </div>

                            {/* Request Body Section */}
                            <div className="border border-border rounded-lg p-4">
                                <h3 className="text-lg font-semibold text-darkGrayBg mb-4">📝 Request Body</h3>
                                <div className="bg-darkGrayBg text-primary text-blue-400 p-4 rounded font-mono text-sm overflow-x-auto">
                                    <pre>{JSON.stringify(requestBody, null, 2)}</pre>
                                </div>
                            </div>

                            {/* Transaction Details Section */}
                            <div className="border border-border rounded-lg p-4">
                                <h3 className="text-lg font-semibold text-darkGrayBg mb-4">🔄 Transaction Details</h3>
                                <div className="bg-darkGrayBg text-primary text-yellow-400 p-4 rounded font-mono text-sm overflow-x-auto">
                                    <pre>{JSON.stringify(hookTransaction, null, 2)}</pre>
                                </div>
                            </div>

                            {/* Transaction Summary */}
                            <div className="border border-border rounded-lg p-4">
                                <h3 className="text-lg font-semibold text-darkGrayBg mb-4">📊 Transaction Summary</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Order ID</label>
                                        <div className="p-3 bg-navBtn rounded border border-border font-mono text-sm">
                                            {hookTransaction.orderId || 'N/A'}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Coupon Code</label>
                                        <div className="p-3 bg-navBtn rounded border border-border font-mono text-sm">
                                            {hookTransaction.couponCode || 'N/A'}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                                        <div className="p-3 bg-navBtn rounded border border-border text-sm">
                                            {hookTransaction.productName || 'N/A'}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Brand Name</label>
                                        <div className="p-3 bg-navBtn rounded border border-border text-sm">
                                            {hookTransaction.brandName || 'N/A'}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Sales Value</label>
                                        <div className="p-3 bg-navBtn rounded border border-border text-sm">
                                            {hookTransaction.salesValue ? `₹${hookTransaction.salesValue}` : 'N/A'}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                                        <div className="p-3 bg-navBtn rounded border border-border text-sm">
                                            {hookTransaction.locationCity || 'N/A'}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
                                        <div className="p-3 bg-navBtn rounded border border-border font-mono text-sm">
                                            {hookTransaction.invoiceNumber || 'N/A'}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Redemption Date</label>
                                        <div className="p-3 bg-navBtn rounded border border-border text-sm">
                                            {hookTransaction.redemption ? new Date(hookTransaction.redemption).toLocaleString() : 'N/A'}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
                                        <div className="p-3 bg-navBtn rounded border border-border font-mono text-sm">
                                            {hookTransaction.userId || 'N/A'}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Created</label>
                                        <div className="p-3 bg-navBtn rounded border border-border text-sm">
                                            {hookTransaction.createdAt ? new Date(hookTransaction.createdAt).toLocaleString() : 'N/A'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Show message if no transaction data */}
                    {transaction.isRedeemed && !hasTransactionData && (
                        <div className="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
                            <h3 className="text-lg font-semibold text-yellow-800 mb-2">⚠️ No Transaction Data</h3>
                            <p className="text-yellow-700">This coupon is marked as redeemed but no detailed transaction data is available.</p>
                        </div>
                    )}

                    {/* Show message for non-redeemed coupons */}
                    {!transaction.isRedeemed && (
                        <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                            <h3 className="text-lg font-semibold text-blue-800 mb-2">ℹ️ Coupon Status</h3>
                            <p className="text-blue-700">This coupon is assigned to the user but has not been redeemed yet. Transaction data will be available once the coupon is redeemed.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserCoupons;